'use client';

import { usePathname } from 'next/navigation';
import { isServer } from '@frameio/utils/config';
import { isValidHref } from './helpers/isValidHref';
import { isAllCollectionsPageType } from './pages/allCollections';
import { isAllSharesPageType } from './pages/allShares';
import { isAuthCallbackPageType } from './pages/authcallback';
import { isAuthenticatingPageType } from './pages/authenticating';
import { isC2CConnectionsPageType } from './pages/c2cConnections';
import {
  isCollectionPageType,
  isCollectionViewerPageType,
} from './pages/collection';
import { isExportPickerPageType } from './pages/exportPicker';
import { isFinderPageType } from './pages/finder';
import { isFolderPageType } from './pages/folder';
import { isFolderViewerPageType } from './pages/folderViewer';
import { isInboxPageType } from './pages/inbox';
import { isLogoutPageType } from './pages/logout';
import { isPairPageType } from './pages/pair';
import { isPrintPageType } from './pages/print';
import { isPromotionsPageType } from './pages/promotions';
import { isSearchPageType } from './pages/search';
import { isSequencesPageType } from './pages/sequences';
import { isSettingsPageType } from './pages/settings';
import {
  isShareBuilderPageType,
  isShareBuilderViewerPageType,
} from './pages/shareBuilder';
import { isShareReviewFinderPageType } from './pages/shareReview';
import { isShareReviewReelPageType } from './pages/shareReviewReel';
import { isTrashPageType } from './pages/trash';
import { isUnauthenticatedPageType } from './pages/unauthenticated';
import { isUploadLinkPublicPageType } from './pages/uploadLinkPublic';
import { isUploadLinksPageType } from './pages/uploadLinks';
import { isWorkspacesPageType } from './pages/workspaces';
import type { PageType, ValidHref } from './types';

export const getPageType = (href: ValidHref | string): PageType => {
  if (!isValidHref(href)) {
    throw new Error('Cannot get page type for unknown href');
  }

  if (isAllSharesPageType(href)) return 'allShares';
  if (isAuthenticatingPageType(href)) return 'authenticating';
  if (isC2CConnectionsPageType(href)) return 'c2cConnections';
  if (isUploadLinksPageType(href)) return 'uploadLinks';
  if (isUploadLinkPublicPageType(href)) return 'uploadLinkPublic';
  if (isCollectionViewerPageType(href)) return 'collectionViewer';
  if (isAllCollectionsPageType(href)) return 'allCollections';
  if (isCollectionPageType(href)) return 'collection';
  if (isExportPickerPageType(href)) return 'exportPicker';
  if (isFinderPageType(href)) return 'finder';
  if (isFolderViewerPageType(href)) return 'folderViewer';
  if (isFolderPageType(href)) return 'folder';
  if (isInboxPageType(href)) return 'inbox';
  if (isPairPageType(href)) return 'pair';
  if (isPrintPageType(href)) return 'print';
  if (isPromotionsPageType(href)) return 'promotions';
  if (isSearchPageType(href)) return 'search';
  if (isSequencesPageType(href)) return 'sequences';
  if (isSettingsPageType(href)) return 'settings';
  if (isShareBuilderViewerPageType(href)) return 'shareBuilderViewer';
  if (isShareBuilderPageType(href)) return 'shareBuilder';
  if (isShareReviewReelPageType(href)) return 'shareReviewReel';
  if (isShareReviewFinderPageType(href)) return 'shareReviewFinder';
  if (isTrashPageType(href)) return 'trash';
  if (isWorkspacesPageType(href)) return 'workspaces';
  if (isUnauthenticatedPageType(href)) return 'unauthenticated';
  if (isLogoutPageType(href)) return 'logout';
  if (isAuthCallbackPageType(href)) return 'authCallback';

  if (!isServer) throw new Error(`Unknown page type for href: ${href}`);
  return null as unknown as PageType;
};

export const useCurrentPageType = (): PageType => {
  const href = usePathname();
  try {
    return getPageType(href);
  } catch (error) {
    // Silence warnings in tests
    if (process.env.NODE_ENV !== 'test') {
      // eslint-disable-next-line no-console
      console.warn(error);
    }
    return null;
  }
};
